<template>
  <div
    class="dropdown pos-relative"
    v-click-outside="clickedOutside"
    @click.stop="trigger === 'click' ? (isOpen = !isOpen) : null"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <slot name="label">
      <div v-if="isComponentLabel" class="w-100 h-100 flex-center flex">
        <component :is="label.component" v-bind="label.props" :class="label.class"></component>
      </div>
      <div v-else>
        {{ label }}
      </div>
    </slot>
    <Transition name="dropdown">
      <div
        v-if="isOpen"
        :style="contentPositionStyle"
        class="dropdown-content"
      >
        <!-- Содержимое получает `close` и закрывает меню само, когда пункт
             выбран. Так же это отдают Headless UI и другие headless-меню:
             дропдаун не гадает по разметке, что было нажато. -->
        <slot :close="close"></slot>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import clickOutside from '../FieldPhone/click-outside.js';

let vClickOutside = clickOutside

const props = defineProps({
  label: {
    type: [String, Object],
    default: 'Open'
  },
  align: {
    type: String,
    default: 'left'
  },
  trigger: {
    type: String,
    default: 'click'
  }
})

const isOpen = ref(false);
const isComponentLabel = computed(() => typeof props.label === 'object');

// align: 'left' | 'right' | 'center'. Точка, из которой вырастает меню при
// открытии, — тот же угол, к которому оно прижато: справа меню растёт из
// правого угла, под кнопкой, а не из противоположного.
const contentPositionStyle = computed(() => {
  if (props.align === 'center') {
    return { left: '50%', right: 'auto', transform: 'translateX(-50%)', transformOrigin: 'top center' };
  }
  return {
    left: props.align === 'left' ? '0' : 'auto',
    right: props.align === 'right' ? '0' : 'auto',
    transformOrigin: props.align === 'right' ? 'top right' : 'top left',
  };
});

let closeTimeout = null;

function handleMouseEnter() {
  if (props.trigger === 'hover') {
    clearTimeout(closeTimeout);
    isOpen.value = true;
  }
}

function handleMouseLeave() {
  if (props.trigger === 'hover') {
    closeTimeout = setTimeout(() => {
      isOpen.value = false;
    }, 100);
  }
}

function clickedOutside() {
  if (props.trigger === 'click') {
    isOpen.value = false;
  }
}

// Закрытие снаружи: уходит в слот и в шаблон родителя через ref. Нужно, когда
// человек выбрал пункт и ушёл на другую страницу — открытое меню поверх неё
// мусор. Решает содержимое: в дропдауне бывают и формы, там клик не значит
// «ушли».
function close() {
  clearTimeout(closeTimeout);
  isOpen.value = false;
}

// isOpen — наружу для вида кнопки-поля: открытое меню подсвечивает рамку
// (FieldDate).
defineExpose({ close, isOpen });
</script>

<style>
.dropdown-content {
  position: absolute;
  box-shadow: 0px 8px 16px 0px rgba(var(--shadow), 0.2);
  z-index: 100;
  border-radius: var(--small);
  backdrop-filter: blur(var(--small));
  -webkit-backdrop-filter: blur(var(--small));
  background: rgba(var(--bg-base), 0.5);
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: transform 0.33s cubic-bezier(0.9, 0, 0.1, 1), opacity 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0.01;
  transform: translateY(-4px) scale(0.8);
}
</style>
